import { Component, OnInit, QueryList, ViewChildren } from "@angular/core";
import { FormGroup, FormBuilder } from "@angular/forms";
import { Store, select } from "@ngrx/store";
import { finalize, Observable, skip, take, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import {
  NgbdSortableHeader,
  SortEvent,
} from "src/app/shared/directive/sortable.directive";
import { IColumn } from "src/app/shared/models/table/column.interface";
import {
  RoleEnum,
  RoleEnumMapping,
} from "src/app/shared/models/user/role.interface";
import { FilterUserHistoryInterface } from "src/app/shared/models/user-history/filter-user-history.interface";
import { AppStateInterface } from "src/app/store/appState.interface";
import {
  invokeUsersHistory,
  setUsersHistory,
  setUsersHistoryFilter,
} from "src/app/store/userHistory/userHistory.actions";
import { initialState } from "src/app/store/userHistory/userHistory.reducers";
import {
  usersHistoryFilterSelector,
  usersHistoryLoadingSelector,
  usersHistoryResultSelector,
} from "src/app/store/userHistory/userHistory.selectors";
import { ResultUserHistoryInterface } from "src/app/shared/models/user-history/result-user-history.interface";
import { invokeApiDistroyed } from "src/app/store/apiState.interface";
import { BadgeService } from "src/app/shared/services/badge.service";
import {
  FilterInterface,
  historyFilter,
} from "src/app/shared/data/filter-data";
import { SaveUserHistoryService } from "src/app/shared/services/history/saveHistory";
import { UserHistoryActionsEnum } from "src/app/shared/enum/userHistoryActions";
import { UserHistoryServicesEnum } from "src/app/shared/enum/userHistoryService";
import { catchForbiddenError } from "src/app/store/user/user.actions";
import { catchForbiddenErrorSelector } from "src/app/store/user/user.selectors";
import { Authorities } from "src/app/shared/enum/authorities";
import { JwtService } from "src/app/shared/services/jwt.service";
import { RefreshButtonState } from "src/app/shared/enum/refreshButtonState";

const FILTER_PAG_REGEX = /[^0-9]/g;
import { FiltersService } from "src/app/shared/services/filters.service";
import { FilterParams } from "src/app/shared/models/filterParams";

@Component({
  selector: "app-logger",
  templateUrl: "./logger.component.html",
})
export class LoggerComponent extends BaseComponent implements OnInit {
  @ViewChildren(NgbdSortableHeader) headers!: QueryList<NgbdSortableHeader>;
  pageSize: number = 15;
  pageNumber: number = 1;
  sortedColumns: IColumn[] = [
    {
      key: "email",
      label: "admin.history.email",
      sorter: true,
    },
  ];
  columns: IColumn[] = [
    {
      key: "companyName",
      label: "admin.history.companyName",
      sorter: false,
    },
    {
      key: "roleName",
      label: "admin.history.role",
      sorter: false,
    },
    {
      key: "ipAddress",
      label: "admin.history.address",
      sorter: false,
    },
    {
      key: "action",
      label: "Action",
      sorter: false,
    },
    {
      key: "endPoint",
      label: "admin.history.service",
      sorter: false,
    },
  ];
  dateColumn: IColumn[] = [
    {
      key: "creationDate",
      label: "admin.history.date",
      sorter: false,
    },
  ];
  filter: FilterUserHistoryInterface = {
    ...initialState.filter,
    pager: {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
    },
  };
  loading$: Observable<boolean | null>;
  formFilter: FormGroup;
  roleEnumMapping = RoleEnumMapping;
  historyResult$: Observable<ResultUserHistoryInterface>;
  historyResult: ResultUserHistoryInterface = {
    count: 0,
    history: [],
    cachedAt: null,
  };
  historyFilter$: Observable<FilterUserHistoryInterface>;
  cachedAt: Date | null;
  historyFilter: FilterInterface = historyFilter;

  catchForbiddenError$: Observable<boolean | null>;
  forbidden: boolean | null = false;
  loading: boolean | null;
  fetchingHistoryState: RefreshButtonState = RefreshButtonState.IDLE

  filters: FilterParams | null;
  constructor(
    private store: Store<AppStateInterface>,
    private fb: FormBuilder,
    public badgeService: BadgeService,
    private saveHistory: SaveUserHistoryService,
    private jwtService: JwtService,
    private filtersService: FiltersService
  ) {
    super(store);
    this.catchForbiddenError$ = this.store
      .pipe(select(catchForbiddenErrorSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.historyResult$ = this.store
      .pipe(select(usersHistoryResultSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.historyFilter$ = this.store
      .pipe(select(usersHistoryFilterSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.loading$ = this.store
      .pipe(select(usersHistoryLoadingSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.formFilter = this.fb.group({
      emailOrNameOrCompanyName: [null],
      date: [[null, null]],
    });

    const authorities = this.jwtService.getPermissions(jwtService.getToken());
    if (authorities) {
      this.forbidden = !authorities.includes(Authorities.H_GETALL.toString());
    }

    this.extractFilters();
  }

  extractFilters() {
    this.filters = this.filtersService.decodeUri();
    let role: RoleEnum | null = null;
    if (this.filters) {
      role = this.findOption(this.filters.status) ?? null;
    }
    this.pageNumber = this.filtersService.extractValueFromUrl(
      this.filters,
      "page",
      this.pageNumber
    );
    this.pageSize = this.filtersService.extractValueFromUrl(
      this.filters,
      "size",
      this.pageSize
    );


    this.filtersService.patchFormValue(
      this.filters,
      ["emailOrNameOrCompanyName", "date"],
      this.formFilter
    );

    this.filter = {
      ...this.filter,
      emailOrNameOrCompanyName: this.formFilter.get("emailOrNameOrCompanyName")?.value,
      startDate: this.formFilter.get("date")?.value[0],
      endDate: this.formFilter.get("date")?.value[1],
      roleName: role
    };
  }

  ngOnInit(): void {
    this.loading$.subscribe((data) => {
      {
        this.loading = data;
      }
    });
    this.catchForbiddenError$.subscribe((result) => {
      if (result) {
        this.loading = false;
        this.store.dispatch(catchForbiddenError({ forbiddenError: null }));
      }
    });
    this.historyFilter.name.map(
      (e) =>
        (e.data = Object.keys(RoleEnum)
          .filter((value) => isNaN(Number(value)) === true)
          .map(
            (key: string | any) => RoleEnumMapping[key as keyof typeof RoleEnum]
          ))
    );
    this.loading$.subscribe((data) => {});
    this.store.dispatch(
      setUsersHistory({ history: { cachedAt: null, count: 0, history: [] } })
    );
    this.filterData();
    this.saveHistory.saveUserHistory(
      UserHistoryActionsEnum.GET,
      UserHistoryServicesEnum.LIST_HISTORY
    );
    this.historyResult$.subscribe((data) => {
      this.historyResult = data;
      this.cachedAt = data.cachedAt;
    });

    this.historyFilter$.subscribe((filter) => {
      this.filter = filter;
      this.invokeHistory(false);
    });

    this.formFilter.valueChanges.subscribe((form) => {
      this.pageNumber = 1
      this.pageSize = 15
      if (form.emailOrNameOrCompanyName == "")
        form.emailOrNameOrCompanyName = null;
      this.filter = {
        ...this.filter,
        emailOrNameOrCompanyName: form.emailOrNameOrCompanyName,
        startDate: form.date[0],
        endDate: form.date[1],
      };
      this.filterData();
    });
  }

  filterData() {
    this.store.dispatch(invokeApiDistroyed());
    this.store.dispatch(
      setUsersHistoryFilter({
        filter: {
          ...this.filter,
          pager: {
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
          },
        },
      })
    );
  }
  onSort({ column, direction }: SortEvent) {
    this.headers.forEach((header) => {
      if (column != header.sortable) {
        header.direction = "";
      }
    });
    var col: string | null = column;
    var sort = null;
    switch (direction) {
      case "asc":
        sort = true;
        break;
      case "desc":
        sort = false;
        break;
      default:
        sort = null;
        col = null;
    }
    this.filter = {
      ...this.filter,
      sortName: column,
      isAscending: sort,
    };
    this.filterData();
  }

  itemsPerPageChange(pageSize: number) {
    this.pageSize = pageSize;
    this.pageNumber = 1
    this.filter = {
      ...this.filter,
      pager: {
        ...this.filter.pager,
        pageSize: pageSize,
      },
    };
    this.filterData();
  }

  changePage(event: number) {
    this.pageNumber = event;
    this.filter = {
      ...this.filter,
      pager: {
        ...this.filter.pager,
        pageNumber: this.pageNumber,
      },
    };
    this.filterData();
  }

  removeFilter() {
    this.store.dispatch(invokeApiDistroyed());
    this.formFilter.reset({
      emailOrNameOrCompanyName: null,
      date: [null, null],
      roleName: null,
    });
    this.store.dispatch(
      setUsersHistoryFilter({
        filter: {
          ...this.filter,
          emailOrNameOrCompanyName: null,
          roleName: null,
          startDate: null,
          endDate: null,
          pager: {
            pageSize: this.pageSize,
            pageNumber: this.pageNumber,
          },
        },
      })
    );
  }

  invokeHistory(refreshCache: boolean) {
    if (refreshCache) {
      this.fetchingHistoryState = RefreshButtonState.LOADING
    }
    if (refreshCache) {
        this.loading$.subscribe((isLoading) => {
          if (isLoading) {
          this.fetchingHistoryState = RefreshButtonState.LOADING
        } else {
          this.fetchingHistoryState = RefreshButtonState.SUCCESS
          setTimeout(() => this.fetchingHistoryState = RefreshButtonState.IDLE, 2500);
        }
      })
    }
    this.store.dispatch(invokeUsersHistory({ refreshCache: refreshCache }));
  }

  findOption(option: string | null) {
    let filter: RoleEnum | null = null;
    const KeyFound = Object.keys(RoleEnumMapping).find(
      (key) => RoleEnumMapping[key as RoleEnum] === option
    );
    switch (KeyFound) {
      case RoleEnum.ROLE_ADMIN:
        filter = RoleEnum.ROLE_ADMIN;
        break;
      case RoleEnum.ROLE_ADVERTISER:
        filter = RoleEnum.ROLE_ADVERTISER;
        break;
      case RoleEnum.ROLE_LOCATION:
        filter = RoleEnum.ROLE_LOCATION;
        break;
      case RoleEnum.ROLE_FRANCHISE:
        filter = RoleEnum.ROLE_FRANCHISE;
        break;
      default:
        filter = null;
    }
    return filter;
  }
  saveOptionFilter(option: string | null) {
    this.pageNumber = 1
    this.filter = {
      ...this.filter,
      roleName: this.findOption(option),
    };
    this.filterData();
  }
}
